import { Flex, Skeleton, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { HOMEPAGE_STATS } from 'stubs/stats';
import Hint from 'ui/shared/Hint';
import IconSvg from 'ui/shared/IconSvg';

import ChainIndicatorChartContainer from './ChainIndicatorChartContainer';
import ChainIndicatorItem from './ChainIndicatorItem';
import useFetchChartData from './useFetchChartData';
import INDICATORS from './utils/indicators';

const indicators = INDICATORS
  .filter(({ id }) => config.UI.homepage.charts.includes(id))
  .sort((a, b) => {
    if (config.UI.homepage.charts.indexOf(a.id) > config.UI.homepage.charts.indexOf(b.id)) {
      return 1;
    }

    if (config.UI.homepage.charts.indexOf(a.id) < config.UI.homepage.charts.indexOf(b.id)) {
      return -1;
    }

    return 0;
  });

const ChainIndicators = () => {
  const [selectedIndicator, selectIndicator] = React.useState(indicators[0]?.id);
  const indicator = indicators.find(({ id }) => id === selectedIndicator);


  const queryResult = useFetchChartData(indicator);
  const statsQueryResult = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: HOMEPAGE_STATS,
    },
  });
  const [customStats, setCustomStats] = React.useState<any>(null);
  const [loadingCustom, setLoadingCustom] = React.useState(false);
  const [errorCustom, setErrorCustom] = React.useState(false);

  React.useEffect(() => {

    const fetchTokenInfo = async () => {

      try {

        setLoadingCustom(true);

        const res = await fetch(
          "https://dexchart.dancharts.com/token/info/0xBEe33b6B1C3df2c4468510E87d6330daA5709F3E"
        );

        const json: any = await res.json();

        setCustomStats(json.success.data);

        setErrorCustom(false);

      } catch (e) {

        console.error(e);
        setErrorCustom(true);

      } finally {

        setLoadingCustom(false);

      }

    };

    fetchTokenInfo();

  }, []);   // ✅ RUN ONLY ON PAGE LOAD

  const bgColor = useColorModeValue('gray.50', 'whiteAlpha.100');

  if (indicators.length === 0) {
    return null;
  }

  const valueTitle = (() => {

    /*
    |--------------------------------------------------------------------------
    | Custom API values
    |--------------------------------------------------------------------------
    */

    if (loadingCustom) {
      return <Skeleton h="36px" w="215px" />;
    }

    if (errorCustom) {
      return <Text fontSize="xs">Error</Text>;
    }

    if (indicator?.id === "coin_price" && customStats) {

      return (
        <Text fontWeight={700} fontSize="30px" lineHeight="36px">
          {'$' + Number(customStats.price).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6
          })}
        </Text>
      );
    }

    if (indicator?.id === "market_cap" && customStats) {

      return (
        <Text fontWeight={700} fontSize="30px" lineHeight="36px">
          {'$' + Number(customStats.pair.pairInfos.mcap)
            .toLocaleString(undefined, {
              maximumFractionDigits: 2
            })}
        </Text>
      );
    }

    /*
    |--------------------------------------------------------------------------
    | Default stats
    |--------------------------------------------------------------------------
    */

    if (statsQueryResult.isPlaceholderData) {
      return <Skeleton h="36px" w="215px" />;
    }

    if (!statsQueryResult.data) {
      return <Text fontSize="xs">There is no data</Text>;
    }

    return (
      <Text fontWeight={700} fontSize="30px" lineHeight="36px">
        {indicator?.value(statsQueryResult.data)}
      </Text>
    );

  })();

  const valueDiff = (() => {

    /*
    |--------------------------------------------------------------------------
    | Custom API change %
    |--------------------------------------------------------------------------
    */



    if (indicator?.id === "coin_price" && customStats) {

      const diff = customStats.change_pct;

      if (diff === null || diff === undefined) {
        return null;
      }


      const diffColor = diff >= 0 ? 'green.500' : 'red.500';

      return (
        <Flex alignItems="center" ml={2} color={diffColor}>
          <IconSvg
            name="arrows/up-head"
            boxSize={5}
            mr={1}
            transform={diff < 0 ? 'rotate(180deg)' : 'rotate(0)'}
          />
          <Text fontWeight={600} color={diffColor}>
            {Math.abs(diff).toFixed(2)}%
          </Text>
        </Flex>
      );
    }

    /*
    |--------------------------------------------------------------------------
    | Default stats
    |--------------------------------------------------------------------------
    */

    if (!statsQueryResult.data || !indicator?.valueDiff) {
      return null;
    }

    const diff = indicator.valueDiff(statsQueryResult.data);

    if (diff === undefined || diff === null) {
      return null;
    }

    const diffColor = diff >= 0 ? 'green.500' : 'red.500';

    return (
      <Skeleton
        isLoaded={!statsQueryResult.isPlaceholderData}
        display="flex"
        alignItems="center"
        color={diffColor}
        ml={2}
      >
        <IconSvg
          name="arrows/up-head"
          boxSize={5}
          mr={1}
          transform={diff < 0 ? 'rotate(180deg)' : 'rotate(0)'}
        />
        <Text fontWeight={600}>
          {diff}%
        </Text>
      </Skeleton>
    );

  })();

  return (
    <Flex
      px={{ base: 3, lg: 4 }}
      py={3}
      borderRadius="base"
      bgColor={bgColor}
      columnGap={{ base: 3, lg: 4 }}
      rowGap={0}
      flexBasis="50%"
      flexGrow={1}
      alignItems="stretch"
    >
      <Flex flexGrow={1} flexDir="column">
        <Flex alignItems="center">
          <Text fontWeight={500}>{indicator?.title}</Text>
          {indicator?.hint && <Hint label={indicator.hint} ml={1} />}
        </Flex>
        <Flex mb={{ base: 0, lg: 2 }} mt={1} alignItems="end">
          {valueTitle}
          {valueDiff}
        </Flex>
        <ChainIndicatorChartContainer {...queryResult} indicator={indicator} />
      </Flex>
      {indicators.length > 1 && (
        <Flex
          flexShrink={0}
          flexDir="column"
          as="ul"
          borderRadius="lg"
          rowGap="6px"
          m={{ base: 'auto 0', lg: 0 }}
        >
          {indicators.map((indicator) => (
            <ChainIndicatorItem
              key={indicator.id}
              {...indicator}
              isSelected={selectedIndicator === indicator.id}
              onClick={selectIndicator}
              stats={statsQueryResult}
              customStats={customStats}   // ✅ ADD THIS
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default ChainIndicators;
